import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { Route, Routes } from 'react-router-dom';
import UnitsProvider from './Contexts/UnitsContext';
import ConfigProvider from './Contexts/ConfigContext';
import UserProvider from './Contexts/UserContext';
import BannerFpProvider from './Contexts/BannerFpContext';
import Navbar from './Components/Navbar';
import Modalv2Provider from './Contexts/Modalv2Context';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NON_REDIRECTABLE_PATHS } from './core/constants';
//pages=========================
import ConfigGenerator from './pages/UnitConfigGenerator';
import BannerFpConfigGenerator from './pages/BannerFpConfigGenerator';
import SplitDocConfigGenerator from './pages/SplitDocConfigGenerator';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyCode from './pages/VerifyCode';
import ForgotPasswordPage from './pages/ForgotPassword';
import NewPasswordPage from './pages/ForgotPassword/NewPasswordPage';
import Dashboard from './pages/Dashboard';

//mui imports==================
import { createTheme, ThemeProvider } from '@mui/material/styles';
import getDesignTokens from './utilities/theme';
import FvpProvider from './Contexts/FvpConfigContext';
import FvpConfigGenerator from './pages/FvpConfigGenerator';
import AccessControlRouteChecker from './Components/AccessControlRouteChecker';
import { ACCESS_CONTROL_CONSTANTS } from './core/constants';
const { SYSTEMS } = ACCESS_CONTROL_CONSTANTS;

//================================
function App() {
	const navigate = useNavigate();
	const location = useLocation();

	const [themeMode, setThemeMode] = useState(localStorage.getItem('theme') || 'light');

	// The dark mode switch would invoke this method
	const colorMode = useMemo(
		() => ({
			toggleColorMode: () => {
				setThemeMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
			}
		}),
		[]
	);

	const toggleTheme = () => {
		setThemeMode((t) => {
			const newMode = t === 'light' ? 'dark' : 'light';
			localStorage.setItem('theme', newMode);
			return newMode;
		});
	};

	// Update the theme only if the mode changes
	const theme = useMemo(() => createTheme(getDesignTokens(themeMode)), [themeMode]);

	useEffect(() => {
		const paths = ['/login', '/']
		const handleInitialRedirection = () => {
			const currentPath = window.location.pathname;
			const lastRoute = localStorage.getItem('route');
			// Only redirect if on root path or `/login`
			if (paths.includes(currentPath) && !NON_REDIRECTABLE_PATHS.includes(currentPath)) {
				if (lastRoute && lastRoute !== currentPath) {
					navigate(lastRoute);
				} else if (!lastRoute) {
					navigate('/dashboard');
				}
			}
		};
		handleInitialRedirection();
	}, []);
	
	useEffect(() => {
		const currentPath = location.pathname;

		const pathsToIgnoreInHistory = ['/forgot-password', '/verify-code'];

		for (const path of pathsToIgnoreInHistory) {
			const regex = new RegExp(`^${path}`);
			if (regex.test(currentPath)) {
				return;
			}
		}

		if (!NON_REDIRECTABLE_PATHS.includes(currentPath)) {
			localStorage.setItem('route', location.pathname);
		}
	}, [location.pathname]);

	return (
		<>
			<UserProvider>
				<Modalv2Provider>
					<ThemeProvider theme={theme}>
						<Navbar />
						<Routes>
							<Route
								path="/update-config/:siteId"
								exact
								element={
									<AccessControlRouteChecker>
										<UnitsProvider>
											<ConfigProvider>
												<ConfigGenerator />
											</ConfigProvider>
										</UnitsProvider>
									</AccessControlRouteChecker>
								}
							/>

							<Route
								path="/create-config/"
								exact
								element={
									<AccessControlRouteChecker>
										<UnitsProvider>
											<ConfigProvider>
												<ConfigGenerator />
											</ConfigProvider>
										</UnitsProvider>
									</AccessControlRouteChecker>
								}
							/>

							<Route
								path="/banner-fp/create-config"
								exact
								element={
									<AccessControlRouteChecker system={SYSTEMS.BANNER_FP}>
										<BannerFpProvider>
											<BannerFpConfigGenerator />
										</BannerFpProvider>
									</AccessControlRouteChecker>
								}
							/>

							<Route
								path="/banner-fp/update-config/:siteId"
								exact
								element={
									<AccessControlRouteChecker system={SYSTEMS.BANNER_FP}>
										<BannerFpProvider>
											<BannerFpConfigGenerator />
										</BannerFpProvider>
									</AccessControlRouteChecker>
								}
							/>
							<Route
								path="/fvp-editor/create-config"
								exact
								element={
									<AccessControlRouteChecker system={SYSTEMS.FVP_PLAYER}>
										<FvpProvider>
											<FvpConfigGenerator />
										</FvpProvider>
									</AccessControlRouteChecker>
								}
							/>
							<Route
								path="/fvp-editor/update-config/:siteId"
								exact
								element={
									<AccessControlRouteChecker system={SYSTEMS.FVP_PLAYER}>
										<FvpProvider>
											<FvpConfigGenerator />
										</FvpProvider>
									</AccessControlRouteChecker>
								}
							/>

							<Route
								path="/splitDoc/update-config/:siteId"
								exact
								element={
									<AccessControlRouteChecker system={SYSTEMS.SPLIT_DOC}>
										<SplitDocConfigGenerator />
									</AccessControlRouteChecker>
								}
							/>

							<Route
								path="/splitDoc/create-config/:siteId"
								exact
								element={
									<AccessControlRouteChecker system={SYSTEMS.SPLIT_DOC}>
										<SplitDocConfigGenerator />
									</AccessControlRouteChecker>
								}
							/>

							<Route path="/forgot-password" exact element={<ForgotPasswordPage />} />

							<Route path="/forgot-password/new-password" exact element={<NewPasswordPage />} />

							<Route path="/verify-code" exact element={<VerifyCode />} />

							<Route path="/login" exact element={<Login />} />

							<Route path="/register" exact element={<Register />} />

							<Route
								path="/dashboard"
								exact
								element={
									<AccessControlRouteChecker system={SYSTEMS.HOME_PAGE}>
										<Dashboard />
									</AccessControlRouteChecker>
								}
							/>
						</Routes>
					</ThemeProvider>
				</Modalv2Provider>
			</UserProvider>
			<ToastContainer />
		</>
	);
}

export default App;
